import { motion } from 'framer-motion';
import type { HarmonicAverage } from '../../lib/coach-dashboard';

interface CoachHarmonicOverviewProps {
  data: HarmonicAverage[];
  locale?: string;
}

function getLevelColor(levelId: number): string {
  if (levelId <= 3) return 'bg-gray-40';
  if (levelId <= 5) return 'bg-orange-40';
  if (levelId <= 7) return 'bg-brand-40';
  if (levelId <= 9) return 'bg-teal-50';
  return 'bg-success-40';
}

function getLevelBg(levelId: number): string {
  if (levelId <= 3) return 'bg-gray-10';
  if (levelId <= 5) return 'bg-orange-10';
  if (levelId <= 7) return 'bg-brand-10';
  if (levelId <= 9) return 'bg-teal-10';
  return 'bg-success-10';
}

export function CoachHarmonicOverview({ data, locale }: CoachHarmonicOverviewProps) {
  const tr = locale === 'tr';
  if (data.length === 0) {
    return (
      <div className="freud-card-elevated p-6">
        <h3 className="text-text-xl text-gray-80 mb-4">{tr ? 'Harmonik Olcek Ortalamalar' : 'Harmonic Scale Averages'}</h3>
        <p className="text-text-sm text-gray-40 text-center py-8">
          {tr ? 'Henuz degerlendirme verisi yok' : 'No assessment data available yet'}
        </p>
      </div>
    );
  }

  const topLevels = [...data].sort((a, b) => b.average - a.average).slice(0, 3);

  return (
    <div className="freud-card-elevated p-6">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-text-xl text-gray-80">{tr ? 'Harmonik Olcek Ortalamalar' : 'Harmonic Scale Averages'}</h3>
      </div>
      <p className="text-text-sm text-gray-40 mb-5">
        Average emotional state distribution across respondents
      </p>

      <div className="mb-6 flex flex-wrap gap-2">
        {topLevels.map((level, i) => (
          <div
            key={level.levelId}
            className={`px-3 py-1.5 rounded-full text-text-xs font-medium ${
              i === 0
                ? 'bg-success-10 text-success-70'
                : 'bg-gray-10 text-gray-60'
            }`}
          >
            {i === 0 ? 'Dominant: ' : ''}{level.name} ({level.average}%)
          </div>
        ))}
      </div>

      <div className="space-y-2">
        {data.map((level, index) => (
          <div key={level.levelId} className="flex items-center gap-3">
            <span className="text-text-xs text-gray-50 w-[120px] flex-shrink-0 truncate">
              {level.levelId}. {level.name}
            </span>
            <div className={`flex-1 h-5 ${getLevelBg(level.levelId)} rounded-full overflow-hidden`}>
              <motion.div
                className={`h-full ${getLevelColor(level.levelId)} rounded-full`}
                initial={{ width: 0 }}
                animate={{ width: `${Math.max(level.average, 2)}%` }}
                transition={{ duration: 0.5, delay: index * 0.04 }}
              />
            </div>
            <span className="text-text-xs text-gray-50 font-medium w-10 text-right">
              {level.average}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
